import { zodResolver } from '@hookform/resolvers/zod';
import { ExtendedAddress } from '@porterin/booking-flow/src/order-review/domain/entities/ExtendedAddress';
import { Waypoint } from '@porterin/booking-flow/src/order-review/domain/entities/Waypoint';
import WaypointIcon from '@porterin/booking-flow/src/order-review/view/components/WaypointIcon';
import {
  TextInput,
  Button,
} from '@porterin/component-library/src/base-components';
import { LocationAccuracyWrapper } from '@porterin/component-library/src/location-accuracy/components/LocationAccuracyWrapper';
import Image from 'next/image';
import React from 'react';
import { useForm } from 'react-hook-form';

import { CityContextType } from '../domain/context/CityContext';
import { CityContextProvider } from '../domain/context/CityProvider';
import { useFareEstimateContent } from '../domain/context/FareEstimateContentContext';
import { AuthBinding } from '../domain/entities/AuthBinding';

import {
  submitFareEstimateFormData,
  stringToFormType,
  prefillAuthData,
  getInitialValues,
  isDesktop,
} from './FareEstimateHelper';
import FareEstimateRequirementWrapperCmp from './FareEstimateRequirementInputCmp';
import { FormData, makeSchema } from './FareEstimateValidationSchema';
import { useLocaleContext } from '@porterin/component-library/src/contexts/locale/LocaleContext';
import { LocaleProviderWrapper } from '@porterin/component-library/src/providers/LocaleProviderWrapper';
import { publishEventParameter } from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import { GaEventsLocationAccuracy } from '@porterin/component-library/src/location-accuracy/store/LocationAccuracyStore';
import CallbackConsentCheckbox from './CallbackConsentCheckbox';
import { debounce } from '@porterin/lead-flow/src/helpers/fare-estimate-results/FareEstimateResultsHelper';
interface FareEstimateFormProps {
  category: string;
  city_context?: CityContextType;
  auth_data?: AuthBinding;
  on_lead_success_handler: (show: boolean) => void;
}

export const FareEstimateFormCmp: React.FC<FareEstimateFormProps> = ({
  category,
  city_context,
  auth_data,
  on_lead_success_handler,
}) => {
  return (
    <LocaleProviderWrapper>
      <FareEstimateFormContentCmp
        category={category}
        city_context={city_context}
        auth_data={auth_data}
        on_lead_success_handler={on_lead_success_handler}
      />
    </LocaleProviderWrapper>
  );
};

const FareEstimateFormContentCmp: React.FC<FareEstimateFormProps> = ({
  category,
  city_context,
  auth_data,
  on_lead_success_handler,
}) => {
  const { country, configs } = useLocaleContext();
  const content = useFareEstimateContent();
  const fe = content.fare_estimate_content;
  const dynamicSchema = makeSchema(
    country.mobileRegex,
    country.consentForCallback,
    country.prefixesToRemove,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    trigger,
    watch,
  } = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(dynamicSchema),
    defaultValues: getInitialValues(auth_data),
  });
  const [formError, setFormError] = React.useState<string | null>(null);
  const [showLocationAccuracy, setShowLocationAccuracy] =
    React.useState<Waypoint | null>(null);

  React.useEffect(() => {
    prefillAuthData(setValue, auth_data);
  }, [auth_data]);

  const onSubmit = (data: FormData) => {
    let callback = (error: string) => {
      setFormError(error);
    };
    submitFareEstimateFormData(
      data,
      setError,
      callback,
      stringToFormType(category),
      city_context?.category,
      auth_data?.isAuthenticated,
      configs.bookingFlowConfig?.isMobileBkfEnabled,
      on_lead_success_handler,
    );
  };
  const pickup = watch('pickup');
  const drop = watch('drop');

  const updateLocation = (
    type: 'pickup' | 'drop',
    address: ExtendedAddress,
  ) => {
    setValue(
      type,
      {
        landmark: address.landmark,
        location: address.location,
      },
      { shouldValidate: true },
    );
  };

  const onClickLocationInput = (waypoint: 'Pickup' | 'Drop') => {
    publishEventParameter(
      waypoint === 'Pickup'
        ? GaEventsLocationAccuracy.fePickupFieldClicked
        : GaEventsLocationAccuracy.feDropFieldClicked,
      {
        category: 'Location Accuracy',
        device: isDesktop() ? 'Desktop' : 'mobile',
      },
    );
  };

  const createDebouncedAnalytics = (event: any) =>
    debounce(
      () =>
        publishEventParameter(event, {
          category: 'Location Accuracy',
          device: isDesktop() ? 'Desktop' : 'mobile',
        }),
      3000,
    );

  const debouncedPublishPhoneNumberAdded = React.useMemo(
    () => createDebouncedAnalytics(GaEventsLocationAccuracy.fePhoneNumberAdded),
    [],
  );

  const debouncedPublishNameAdded = React.useMemo(
    () => createDebouncedAnalytics(GaEventsLocationAccuracy.feNameAdded),
    [],
  );

  const callPublishEventParameter = (event: string) => {
    publishEventParameter(event, {
      category: 'Location Accuracy',
      device: isDesktop() ? 'Desktop' : 'mobile',
    });
  };

  React.useEffect(() => {
    return () => {
      (debouncedPublishPhoneNumberAdded as any)?.cancel?.();
      (debouncedPublishNameAdded as any)?.cancel?.();
    };
  }, [debouncedPublishPhoneNumberAdded, debouncedPublishNameAdded]);

  return (
    <CityContextProvider
      value={
        city_context ?? {
          citySelected: undefined,
          selectedCityLabel: undefined,
          setCitySelected: () => {},
          category: 'Home',
          queryParams: '',
        }
      }
    >
      <div id="fare-estimate-component" className="flex w-full normal-case">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full"
          autoComplete="off"
        >
          <TextInput
            identifiers={{ id: 'pickup' }}
            onClick={() => {
              setShowLocationAccuracy('Pickup');
              onClickLocationInput('Pickup');
            }}
            inputOptions={{
              placeholder: fe.field_params.pickup.placeholder,
              error: errors.pickup ? fe.field_params.pickup.error_msg : undefined,
            }}
            value={pickup?.landmark ?? ''}
            icons={{
              leftIcon: <WaypointIcon waypoint={'Pickup'} />,
            }}
          />
          <div className="h-5 ml-8 w-0 " />
          <TextInput
            identifiers={{ id: 'drop' }}
            className="mb-6"
            onClick={() => {
              setShowLocationAccuracy('Drop');
              onClickLocationInput('Drop');
            }}
            inputOptions={{
              placeholder: fe.field_params.drop.placeholder,
              error: errors.drop ? fe.field_params.drop.error_msg : undefined,
            }}
            value={drop?.landmark ?? ''}
            readOnly
            icons={{
              leftIcon: <WaypointIcon waypoint={'Drop'} />,
            }}
          />
          {!auth_data?.isAuthenticated && pickup?.landmark && drop?.landmark ? (
            <TextInput
              identifiers={{ id: 'mobile' }}
              inputOptions={{
                placeholder: fe.field_params.phone.placeholder,
                error: errors.mobile ? fe.field_params.phone.error_msg : undefined,
              }}
              onClick={() => {
                callPublishEventParameter(
                  GaEventsLocationAccuracy.fePhoneNumberFieldClicked,
                );
              }}
              className="my-6"
              icons={{
                leftIcon: (
                  <Image
                    src={fe.field_params.phone.icon.url}
                    height={16}
                    width={16}
                    alt={fe.field_params.phone.icon.alt}
                  />
                ),
              }}
              {...register('mobile', {
                onChange: _e => {
                  debouncedPublishPhoneNumberAdded();
                  if (errors.mobile) {
                    trigger('mobile');
                  }
                },
              })}
            />
          ) : null}
          {!auth_data?.isAuthenticated && pickup?.landmark && drop?.landmark ? (
            <TextInput
              identifiers={{ id: 'name' }}
              inputOptions={{
                placeholder: fe.field_params.name.placeholder,
                error: errors.name ? fe.field_params.name.error_msg : undefined,
              }}
              className="my-6"
              icons={{
                leftIcon: (
                  <Image
                    src={fe.field_params.name.icon.url}
                    height={16}
                    width={16}
                    alt={fe.field_params.name.icon.alt}
                  />
                ),
              }}
              onClick={() => {
                callPublishEventParameter(
                  GaEventsLocationAccuracy.feNameFieldClicked,
                );
              }}
              {...register('name', {
                onChange: _e => {
                  debouncedPublishNameAdded();
                  if (errors.name) {
                    trigger('name');
                  }
                },
              })}
            />
          ) : null}
          {!auth_data?.isAuthenticated && pickup?.landmark && drop?.landmark ? (
            <FareEstimateRequirementWrapperCmp
              identifiers={{ id: 'requirement' }}
              error={
                errors.requirement
                  ? fe.field_params.requirement.error_msg
                  : undefined
              }
              selectRequirement={req => {
                if (req) {
                  callPublishEventParameter(
                    GaEventsLocationAccuracy.feUserTypeSelected,
                  );
                  setValue('requirement', req, {
                    shouldValidate: true,
                  });
                }
              }}
            />
          ) : null}
          <CallbackConsentCheckbox
            label={fe.consent_label}
            form={{ register, trigger }}
            class_name="items-start m-[2rem_0_1.2rem_0]"
            should_show={
              (!auth_data?.isAuthenticated &&
                pickup?.landmark &&
                drop?.landmark &&
                country.consentForCallback?.shouldShow) ||
              false
            }
          />
          <Button
            identifiers={{ id: 'submit-button' }}
            className={`w-full my-4 text-[#ffffff] transition-colors duration-300 bg-[#0a57ff]`}
            interactions={{
              onClick: () => {
                callPublishEventParameter(
                  GaEventsLocationAccuracy.feGetEstimateButtonClicked,
                );
                trigger();
              },
            }}
          >
            {fe.get_estimate_label}
          </Button>
          {formError && <p className="text-red-500">{formError}</p>}
        </form>
      </div>
      {showLocationAccuracy ? (
        <LocationAccuracyWrapper
          is_open={showLocationAccuracy}
          waypoint={showLocationAccuracy}
          close_modal={() => setShowLocationAccuracy(null)}
          on_confirm={
            showLocationAccuracy === 'Pickup'
              ? updateLocation.bind(null, 'pickup')
              : updateLocation.bind(null, 'drop')
          }
        />
      ) : null}
    </CityContextProvider>
  );
};

export default FareEstimateFormCmp;
